import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react";
import { useAgent } from "agents/react";
import { useAgentChat } from "@cloudflare/ai-chat/react";
import type { FileUIPart } from "ai";
import { getModelConfig } from "@/shared/models";
import type { ModelSelectionState as ChatState } from "@/shared/models";
import type {
  EntitySelectionHintDataPart
} from "@/shared/types/entity-chat-parts";
import type {
  EntityActionViewModel,
  EntityCardViewModel
} from "@/shared/types/entity-view";
import type { DebugTraceEvent } from "@/shared/types/debug-trace";
import { useAgentState } from "@/hooks/useAgentState";
import { useMessageHandling } from "@/hooks/useMessageHandling";
import { useChatSendWithAuthRetry } from "@/hooks/chat/useChatSendWithAuthRetry";
import { useDebugMessageIngestion } from "@/hooks/chat/useDebugMessageIngestion";
import type { FeatureFlag } from "@/shared/feature-flags";
import type { ChatUIMessage } from "@/shared/types/chat-ui-message";
import {
  hasRunningPolicyReviewSubAgent,
  getLatestPolicyReviewContextUpdatedAt
} from "@/shared/policy-review-ui-state";
import { postHostEntityNavigation } from "@/shared/host-navigation";
import { prepareChatSendRequest } from "@/shared/chat-request-prep";
import { WelcomeScreen } from "@/components/chat/WelcomeScreen";
import { MessageList } from "@/components/chat/MessageList";
import { ChatInput } from "@/components/chat/ChatInput";
import { normalizeChatStatusForUi } from "@/components/chat/chat-status";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton
} from "@/components/ai-elements/conversation";

const DeveloperTraceDrawer = lazy(async () => {
  const mod = await import("@/components/debug/DeveloperTraceDrawer");
  return { default: mod.DeveloperTraceDrawer };
});

const AUTH_STATE_MESSAGE_TYPE = "claimmosaic_auth_state";

function isRenderableMessagePart(part: unknown): boolean {
  if (!part || typeof part !== "object") {
    return false;
  }

  const candidate = part as { type?: unknown };
  if (typeof candidate.type !== "string") {
    return false;
  }

  if (
    candidate.type === "text" ||
    candidate.type === "reasoning" ||
    candidate.type === "source-url"
  ) {
    return true;
  }

  if (candidate.type === "dynamic-tool" || candidate.type.startsWith("tool-")) {
    return true;
  }

  return (
    candidate.type === "data-entityCards" ||
    candidate.type === "data-entitySummary" ||
    candidate.type === "data-entityNotice"
  );
}

export interface AuthenticatedChatProps {
  sessionName: string;
  showDebug: boolean;
  debugCaptureEnabled: boolean;
  isDebugDrawerOpen: boolean;
  onToggleDebugDrawer: () => void;
  onSetDebugDrawerOpen: (open: boolean) => void;
  debugEvents: DebugTraceEvent[];
  clearDebugEvents: () => void;
  emitClientDebugEvent: (event: Partial<DebugTraceEvent>) => void;
  isEnabled: (flag: FeatureFlag) => boolean;
  accessToken: string | null;
  tokenLoading: boolean;
  tokenError: string | null;
  getAccessToken: (options?: {
    forceRefresh?: boolean;
  }) => Promise<string | null>;
  onLogin: () => Promise<void>;
  canOpenActivityMentions: boolean;
}

export function AuthenticatedChat({
  sessionName,
  showDebug,
  debugCaptureEnabled,
  isDebugDrawerOpen,
  onToggleDebugDrawer,
  onSetDebugDrawerOpen,
  debugEvents,
  clearDebugEvents,
  emitClientDebugEvent,
  isEnabled,
  accessToken,
  tokenLoading,
  tokenError,
  getAccessToken,
  onLogin,
  canOpenActivityMentions
}: AuthenticatedChatProps) {
  const agent = useAgent({
    agent: "chat" as const,
    name: sessionName
  });
  const effectiveAccessToken = accessToken;
  const persistedAgentState = (agent as { state?: Partial<ChatState> }).state;
  const retryAccessTokenRef = useRef<string | null>(null);

  const resolveAuthorizationToken = useCallback(
    async (forceRefresh: boolean): Promise<string | null> =>
      getAccessToken({ forceRefresh }),
    [getAccessToken]
  );

  const prepareSendMessagesRequest = useCallback(
    async (options: {
      id?: string;
      messages?: unknown[];
      trigger?: "submit-message" | "regenerate-message";
      messageId?: string;
      headers?: HeadersInit;
      body?: Record<string, unknown>;
    }) => {
      const token =
        retryAccessTokenRef.current ?? (await resolveAuthorizationToken(false));
      retryAccessTokenRef.current = null;
      if (!token) {
        throw new Error("Unable to retrieve access token.");
      }

      // WebSocket transport in newer ai-chat versions does not forward
      // per-message headers, so publish auth state explicitly before send.
      agent.send(
        JSON.stringify({
          type: AUTH_STATE_MESSAGE_TYPE,
          jwtToken: token,
          debugTraceEnabled: debugCaptureEnabled
        })
      );

      const { messages: rawMessages, ...restOptions } = options;
      const preparedOptions = Array.isArray(rawMessages)
        ? {
            ...restOptions,
            messages: rawMessages as ChatUIMessage[]
          }
        : { ...restOptions };

      return prepareChatSendRequest({
        token,
        debugTraceEnabled: debugCaptureEnabled,
        options: preparedOptions
      });
    },
    [agent, debugCaptureEnabled, resolveAuthorizationToken]
  );

  const { model, setModel } = useAgentState({ agent });

  const {
    messages: agentMessages,
    clearHistory,
    addToolApprovalResponse,
    status,
    stop,
    sendMessage
  } = useAgentChat<
    ChatState,
    ChatUIMessage
  >({
    agent,
    prepareSendMessagesRequest
  });
  const uiStatus = normalizeChatStatusForUi(status);

  const renderableMessages = useMemo(
    () =>
      agentMessages.filter((message) =>
        (message.parts ?? []).some((part) => isRenderableMessagePart(part))
      ),
    [agentMessages]
  );
  const runningPolicyReviewFromLatestAssistant = useMemo(
    () => hasRunningPolicyReviewSubAgent(agentMessages),
    [agentMessages]
  );
  const {
    sendMessageWithAuthRetry,
    sendFailureMessage,
    authFailureMessage,
    clearSendFailureMessage
  } = useChatSendWithAuthRetry({
    sendMessage,
    resolveAuthorizationToken,
    emitClientDebugEvent,
    retryAccessTokenRef
  });

  const { handleSubmit, regenerate } = useMessageHandling({
    sendMessage: sendMessageWithAuthRetry,
    model,
    messages: agentMessages,
    onDebugEvent: emitClientDebugEvent
  });

  const { resetProcessedMessageTracking } = useDebugMessageIngestion({
    status,
    messages: agentMessages,
    enabled: debugCaptureEnabled,
    emitClientDebugEvent
  });

  const handleManualToolCall = useCallback(
    async (toolName: string, args: Record<string, unknown>) => {
      const modelConfig = getModelConfig(model);
      const serializedArgs = JSON.stringify(args, null, 2);

      emitClientDebugEvent({
        category: "tool",
        level: "info",
        title: "Manual tool call requested",
        toolName,
        payload: {
          args
        }
      });

      await sendMessageWithAuthRetry(
        {
          role: "user",
          parts: [
            {
              type: "text",
              text: [
                `DEBUG TOOL TRIGGER: Call the tool "${toolName}" now.`,
                "Use exactly these arguments:",
                serializedArgs,
                "Do not call any other tools in this step."
              ].join("\n")
            }
          ]
        },
        {
          body: {
            selectedModelId: model,
            reasoningEffort: modelConfig?.reasoningEffort
          }
        }
      );
    },
    [sendMessageWithAuthRetry, model, emitClientDebugEvent]
  );

  const launchPolicyReview = useCallback(
    async ({
      objective,
      filePart
    }: {
      objective: string;
      filePart: FileUIPart;
    }) => {
      const modelConfig = getModelConfig(model);
      const launchObjective =
        objective.trim() ||
        "Review this policy and identify coverage, exclusions, and key findings.";

      await sendMessageWithAuthRetry(
        {
          role: "user",
          parts: [
            {
              type: "text",
              text: launchObjective
            },
            {
              type: "data-subAgentLaunch",
              data: {
                agentId: "policy-review",
                objective: launchObjective,
                source: "policy-review-launcher"
              }
            },
            filePart
          ]
        },
        {
          body: {
            selectedModelId: model,
            reasoningEffort: modelConfig?.reasoningEffort
          }
        }
      );
    },
    [sendMessageWithAuthRetry, model]
  );

  const clearPolicyReviewContext = useCallback(() => {
    if (typeof agent.setState !== "function") {
      return;
    }

    const existingMap = persistedAgentState?.clearedSubAgentContextAt ?? {};
    agent.setState({
      ...(persistedAgentState ?? {}),
      clearedSubAgentContextAt: {
        ...existingMap,
        "policy-review": new Date().toISOString()
      }
    });
  }, [agent, persistedAgentState]);

  const clearHistoryAndState = useCallback(() => {
    clearHistory();
    clearPolicyReviewContext();
    resetProcessedMessageTracking();
  }, [clearHistory, clearPolicyReviewContext, resetProcessedMessageTracking]);

  const handleSubmitWithPolicyRouting = useCallback(
    async (...args: Parameters<typeof handleSubmit>) => {
      await handleSubmit(...args);
    },
    [handleSubmit]
  );

  const handleEntitySelect = useCallback(
    async (entity: EntityCardViewModel) => {
      if (!entity.ref) {
        return;
      }

      const entityId = typeof entity.id === "number" ? entity.id : null;
      if (
        entityId !== null &&
        postHostEntityNavigation(entity.entityType, entityId)
      ) {
        emitClientDebugEvent({
          category: "entity",
          level: "info",
          title: "Host navigation requested",
          payload: {
            entityType: entity.entityType,
            entityId,
            source: "entity-card"
          }
        });
        return;
      }

      const modelConfig = getModelConfig(model);
      const selectionHint: EntitySelectionHintDataPart = {
        entityType: entity.entityType,
        ...(typeof entity.id === "number" ? { entityId: entity.id } : {}),
        entityRef: entity.ref,
        source: "card-click"
      };

      emitClientDebugEvent({
        category: "entity",
        level: "info",
        title: "Entity card selected",
        payload: {
          entityType: entity.entityType,
          entityId: entity.id,
          entityRef: entity.ref
        }
      });

      await sendMessageWithAuthRetry(
        {
          role: "user",
          parts: [
            {
              type: "data-entitySelectionHint",
              data: selectionHint
            }
          ]
        },
        {
          body: {
            selectedModelId: model,
            reasoningEffort: modelConfig?.reasoningEffort
          }
        }
      );
    },
    [sendMessageWithAuthRetry, model, emitClientDebugEvent]
  );

  const handleEntityAction = useCallback(
    async (
      action: EntityActionViewModel,
      context: {
        entity?: EntityCardViewModel;
        feedItemId?: number;
        claimId?: number;
      }
    ) => {
      const modelConfig = getModelConfig(model);
      const entity = context.entity;

      emitClientDebugEvent({
        category: "entity",
        level: "info",
        title: "Entity action triggered",
        payload: {
          actionId: action.actionId,
          actionLabel: action.label,
          entityType: entity?.entityType,
          entityId: entity?.id ?? null,
          feedItemId: context.feedItemId ?? null,
          claimId: context.claimId ?? null
        }
      });

      const actionText = [
        `Run action: ${action.actionId}`,
        `Action label: ${action.label}`,
        `Action kind: ${action.kind}`,
        action.requiresApproval ? "This action requires approval." : "",
        entity
          ? `Entity context: ${entity.entityType} ${entity.id ?? "n/a"} (${entity.ref})`
          : "",
        context.feedItemId != null ? `Feed item ID: ${context.feedItemId}` : "",
        context.claimId != null ? `Claim ID: ${context.claimId}` : "",
        "Call the best matching ClaimMosaic tool now."
      ]
        .filter(Boolean)
        .join("\n");

      await sendMessageWithAuthRetry(
        {
          role: "user",
          parts: [
            {
              type: "text",
              text: actionText
            },
            ...(entity
              ? ([
                  {
                    type: "data-entitySelectionHint",
                    data: {
                      entityType: entity.entityType,
                      ...(typeof entity.id === "number"
                        ? { entityId: entity.id }
                        : {}),
                      entityRef: entity.ref,
                      source: "card-click"
                    }
                  }
                ] as const)
              : [])
          ]
        },
        {
          body: {
            selectedModelId: model,
            reasoningEffort: modelConfig?.reasoningEffort
          }
        }
      );
    },
    [sendMessageWithAuthRetry, model, emitClientDebugEvent]
  );

  const handleActivityMentionClick = useCallback(
    async (
      contactId: number,
      context: { feedItemId?: number; claimId?: number }
    ) => {
      if (!canOpenActivityMentions) {
        return;
      }

      const modelConfig = getModelConfig(model);

      emitClientDebugEvent({
        category: "entity",
        level: "info",
        title: "Activity mention clicked",
        payload: {
          contactId,
          feedItemId: context.feedItemId ?? null,
          claimId: context.claimId ?? null
        }
      });

      const mentionActionText = [
        "Open the mentioned contact from the activity feed.",
        `Contact ID: ${contactId}`,
        context.feedItemId != null ? `Feed item ID: ${context.feedItemId}` : "",
        context.claimId != null ? `Claim ID: ${context.claimId}` : "",
        "Call getContactById with this contact ID now."
      ]
        .filter(Boolean)
        .join("\n");

      await sendMessageWithAuthRetry(
        {
          role: "user",
          parts: [
            {
              type: "text",
              text: mentionActionText
            },
            {
              type: "data-entitySelectionHint",
              data: {
                entityType: "contact",
                entityId: contactId,
                entityRef: `Contact ${contactId}`,
                source: "card-click"
              }
            }
          ]
        },
        {
          body: {
            selectedModelId: model,
            reasoningEffort: modelConfig?.reasoningEffort
          }
        }
      );
    },
    [
      canOpenActivityMentions,
      emitClientDebugEvent,
      model,
      sendMessageWithAuthRetry
    ]
  );

  const handleToolApproval = useCallback(
    (approvalId: string, approved: boolean) => {
      emitClientDebugEvent({
        category: "tool",
        level: "info",
        title: "Tool approval response submitted",
        payload: {
          approvalId,
          approved
        }
      });

      addToolApprovalResponse({ id: approvalId, approved });
    },
    [addToolApprovalResponse, emitClientDebugEvent]
  );

  const policyContextClearedAt =
    persistedAgentState?.clearedSubAgentContextAt?.["policy-review"];
  const policyContextUpdatedAt = useMemo(
    () => getLatestPolicyReviewContextUpdatedAt(agentMessages, policyContextClearedAt),
    [agentMessages, policyContextClearedAt]
  );
  const isPolicyContextAvailable = Boolean(policyContextUpdatedAt);

  useEffect(() => {
    emitClientDebugEvent({
      category: "chat",
      level: "info",
      title: "Agent state changed",
      payload: {
        policyContextAvailable: isPolicyContextAvailable,
        policyContextUpdatedAt: policyContextUpdatedAt ?? null,
        policyContextClearedAt: policyContextClearedAt ?? null
      }
    });
  }, [
    emitClientDebugEvent,
    isPolicyContextAvailable,
    policyContextUpdatedAt,
    policyContextClearedAt
  ]);

  if (tokenLoading && !effectiveAccessToken) {
    return (
      <div className="w-full h-screen flex flex-col">
        <div className="flex items-center justify-center flex-1">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground">
              Authorizing agent session...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!effectiveAccessToken) {
    return (
      <div className="w-full h-screen flex flex-col">
        <div className="flex items-center justify-center flex-1">
          <div className="text-center space-y-2 max-w-md px-4">
            <p className="text-red-500">Unable to authorize agent session.</p>
            <p className="text-muted-foreground text-sm">
              {tokenError ?? "Please refresh and try again."}
            </p>
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
              onClick={() => void onLogin()}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex">
      <div className="min-w-0 flex-1 flex flex-col">
        {sendFailureMessage && (
          <div className="px-4 md:px-6 lg:px-8 pt-4">
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700">
              <div className="font-medium">Request Failed</div>
              <div>{sendFailureMessage}</div>
              <button
                type="button"
                className="mt-2 inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
                onClick={clearSendFailureMessage}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        {authFailureMessage && (
          <div className="px-4 md:px-6 lg:px-8 pt-4">
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <div className="font-medium">Authentication Required</div>
              <div>{authFailureMessage}</div>
              <button
                type="button"
                className="mt-2 inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
                onClick={() => void onLogin()}
              >
                Sign In Again
              </button>
            </div>
          </div>
        )}
        <Conversation className="flex-1">
          <ConversationContent className="px-4 md:px-6 lg:px-8">
            {renderableMessages.length === 0 ? (
              <WelcomeScreen
                onSuggestionClick={(suggestion) =>
                  handleSubmitWithPolicyRouting({ text: suggestion, files: [] })
                }
                onPolicyReviewLaunch={launchPolicyReview}
                showDemoRichUI={isEnabled("demo-rich-ui")}
              />
            ) : (
              <MessageList
                messages={renderableMessages}
                status={uiStatus}
                onRegenerate={regenerate}
                isEnabled={isEnabled}
                canOpenActivityMentions={canOpenActivityMentions}
                onActivityMentionClick={handleActivityMentionClick}
                onToolApproval={handleToolApproval}
                onSelectEntity={handleEntitySelect}
                onRunEntityAction={handleEntityAction}
              />
            )}
          </ConversationContent>
          <ConversationScrollButton />
        </Conversation>

        <div className="px-4 md:px-6 lg:px-8 pb-3">
          <ChatInput
            model={model}
            onModelChange={setModel}
            onSubmit={handleSubmitWithPolicyRouting}
            status={uiStatus}
            onStop={stop}
            messages={renderableMessages}
            onClearHistory={clearHistoryAndState}
            isEnabled={isEnabled}
            showDebug={showDebug}
            onManualToolCall={handleManualToolCall}
            isDebugDrawerOpen={isDebugDrawerOpen}
            onToggleDebugDrawer={onToggleDebugDrawer}
            policyContextAvailable={isPolicyContextAvailable}
            onClearPolicyContext={clearPolicyReviewContext}
          />
        </div>
      </div>

      {showDebug && (
        <Suspense fallback={null}>
          <DeveloperTraceDrawer
            open={isDebugDrawerOpen}
            onOpenChange={onSetDebugDrawerOpen}
            events={debugEvents}
            messages={agentMessages}
            clearEvents={clearDebugEvents}
            policyIndicators={{
              policyContextAvailable: isPolicyContextAvailable,
              inferredPolicyRunning: runningPolicyReviewFromLatestAssistant,
              policyContextUpdatedAt: policyContextUpdatedAt ?? null,
              policyContextClearedAt: policyContextClearedAt ?? null
            }}
            onClearPolicyContext={clearPolicyReviewContext}
          />
        </Suspense>
      )}
    </div>
  );
}
